import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ConfirmationModal from './ConfirmationModal';
import Avatar from './Avatar';
import './Admin.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Admin = () => {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [allowedEmails, setAllowedEmails] = useState([]);
  const [chatSettings, setChatSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Forms
  const [newEmail, setNewEmail] = useState('');
  const [bulkEmails, setBulkEmails] = useState('');
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    type: '',
    id: null,
    title: '',
    message: ''
  });

  const navigate = useNavigate();

  useEffect(() => {
    if (activeTab === 'users') {
      loadUsers();
    } else if (activeTab === 'emails') {
      loadAllowedEmails();
    } else if (activeTab === 'settings') {
      loadChatSettings();
    }
  }, [activeTab]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/users`);
      setUsers(response.data.users);
    } catch (error) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadAllowedEmails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/allowed-emails`);
      setAllowedEmails(response.data.allowedEmails);
    } catch (error) {
      setError('Failed to load allowed emails');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmail = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;

    try {
      await axios.post(`${API_BASE_URL}/admin/allowed-emails`, {
        email: newEmail.trim()
      });
      
      setSuccess('Email added successfully');
      setNewEmail('');
      loadAllowedEmails();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to add email');
    }
  };

  const handleBulkAddEmails = async (e) => {
    e.preventDefault();
    if (!bulkEmails.trim()) return;

    const emails = bulkEmails
      .split('\n')
      .map(email => email.trim())
      .filter(email => email);

    try {
      const response = await axios.post(`${API_BASE_URL}/admin/allowed-emails/bulk`, {
        emails
      });
      
      setSuccess(response.data.message);
      setBulkEmails('');
      loadAllowedEmails();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to add emails');
    }
  };

  const handleRemoveEmail = async (emailId) => {
    setConfirmModal({
      isOpen: true,
      type: 'removeEmail',
      id: emailId,
      title: 'Remove Email',
      message: 'Are you sure you want to remove this email from the allowed list?'
    });
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await axios.put(`${API_BASE_URL}/admin/users/${userId}/role`, {
        role: newRole
      });
      
      setSuccess('User role updated successfully');
      loadUsers();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update role');
    }
  };

  const handleMuteUser = async (userId) => {
    const duration = prompt('Mute duration in minutes (leave empty for permanent):');
    if (duration === null) return;

    try {
      await axios.post(`${API_BASE_URL}/admin/users/${userId}/mute`, {
        duration: duration ? parseInt(duration) : null
      });
      
      setSuccess('User muted successfully');
      loadUsers();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to mute user');
    }
  };

  const handleUnmuteUser = async (userId) => {
    try {
      await axios.post(`${API_BASE_URL}/admin/users/${userId}/unmute`);
      setSuccess('User unmuted successfully');
      loadUsers();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to unmute user');
    }
  };

  const loadChatSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/chat-settings`);
      setChatSettings(response.data.settings);
    } catch (error) {
      setError('Failed to load chat settings');
    } finally {
      setLoading(false);
    }
  };

  const toggleAnnouncementMode = async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/admin/announcement-mode`, {
        enabled: !chatSettings.announcementMode
      });
      
      setChatSettings(response.data.settings);
      setSuccess(response.data.message);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to toggle announcement mode');
    }
  };

  const handleRemoveUser = async (userId) => {
    setConfirmModal({
      isOpen: true,
      type: 'removeUser',
      id: userId,
      title: 'Remove User',
      message: 'Are you sure you want to remove this user? This action cannot be undone.'
    });
  };

  const confirmAction = async () => {
    const { type, id } = confirmModal;
    
    try {
      if (type === 'removeEmail') {
        await axios.delete(`${API_BASE_URL}/admin/allowed-emails/${id}`);
        setSuccess('Email removed successfully');
        loadAllowedEmails();
      } else if (type === 'removeUser') {
        await axios.delete(`${API_BASE_URL}/admin/users/${id}`);
        setSuccess('User removed successfully');
        loadUsers();
      }
    } catch (error) {
      setError(error.response?.data?.message || `Failed to ${type === 'removeEmail' ? 'remove email' : 'remove user'}`);
    } finally {
      setConfirmModal({ isOpen: false, type: '', id: null, title: '', message: '' });
    }
  };

  const cancelAction = () => {
    setConfirmModal({ isOpen: false, type: '', id: null, title: '', message: '' });
  };

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  return (
    <div className="admin-container">
      <header className="admin-header">
        <button 
          className="back-btn"
          onClick={() => navigate('/chat')}
        >
          ← Back to Chat
        </button>
        <h1>Admin Panel</h1>
      </header>

      <div className="admin-content">
        <div className="admin-tabs">
          <button 
            className={`tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            Users ({users.length})
          </button>
          <button 
            className={`tab ${activeTab === 'emails' ? 'active' : ''}`}
            onClick={() => setActiveTab('emails')}
          >
            Allowed Emails ({allowedEmails.length})
          </button>
          <button 
            className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            Chat Settings
          </button>
        </div>

        {error && (
          <div className="error-message">
            {error}
            <button onClick={clearMessages} className="close-btn">×</button>
          </div>
        )}

        {success && (
          <div className="success-message">
            {success}
            <button onClick={clearMessages} className="close-btn">×</button>
          </div>
        )}

        <div className="tab-content">
          {activeTab === 'users' && (
            <div className="users-tab">
              <h3>Manage Users</h3>
              
              {loading ? (
                <div className="loading">Loading users...</div>
              ) : (
                <div className="users-table">
                  <table>
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Email</th>
                        <th>Username</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(user => (
                        <tr key={user._id}>
                          <td>
                            <div className="user-info">
                              <Avatar
                                user={user}
                                alt={user.displayName}
                                size={32}
                                className="user-avatar"
                              />
                              <span>{user.displayName}</span>
                            </div>
                          </td>
                          <td>{user.email}</td>
                          <td>{user.username || 'Not set'}</td>
                          <td>
                            <select 
                              value={user.role}
                              onChange={(e) => handleUpdateRole(user._id, e.target.value)}
                              className="role-select"
                            >
                              <option value="member">Member</option>
                              <option value="moderator">Moderator</option>
                              <option value="admin">Admin</option>
                            </select>
                          </td>
                          <td>
                            <span className={`status-badge ${user.isMuted ? 'muted' : 'active'}`}>
                              {user.isMuted ? 'Muted' : 'Active'}
                            </span>
                          </td>
                          <td>
                            <div className="action-buttons">
                              {user.isMuted ? (
                                <button 
                                  onClick={() => handleUnmuteUser(user._id)}
                                  className="action-btn unmute"
                                >
                                  Unmute
                                </button>
                              ) : (
                                <button 
                                  onClick={() => handleMuteUser(user._id)}
                                  className="action-btn mute"
                                >
                                  Mute
                                </button>
                              )}
                              {user.role !== 'admin' && (
                                <button 
                                  onClick={() => handleRemoveUser(user._id)}
                                  className="action-btn remove"
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'emails' && (
            <div className="emails-tab">
              <h3>Manage Allowed Emails</h3>
              
              <div className="email-forms">
                <div className="form-section">
                  <h4>Add Single Email</h4>
                  <form onSubmit={handleAddEmail} className="add-email-form">
                    <input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="student@university.edu"
                      required
                    />
                    <button type="submit">Add Email</button>
                  </form>
                </div>

                <div className="form-section">
                  <h4>Bulk Add Emails</h4>
                  <form onSubmit={handleBulkAddEmails} className="bulk-email-form">
                    <textarea
                      value={bulkEmails}
                      onChange={(e) => setBulkEmails(e.target.value)}
                      placeholder="student1@university.edu&#10;student2@university.edu&#10;student3@university.edu"
                      rows={6}
                    />
                    <button type="submit">Add All Emails</button>
                  </form>
                </div>
              </div>

              {loading ? (
                <div className="loading">Loading emails...</div>
              ) : (
                <div className="emails-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Status</th>
                        <th>Used By</th>
                        <th>Added By</th>
                        <th>Date Added</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allowedEmails.map(email => (
                        <tr key={email._id}>
                          <td>{email.email}</td>
                          <td>
                            <span className={`status-badge ${email.isUsed ? 'used' : 'available'}`}>
                              {email.isUsed ? 'Used' : 'Available'}
                            </span>
                          </td>
                          <td>
                            {email.usedBy ? email.usedBy.displayName : '-'}
                          </td>
                          <td>{email.addedBy.displayName}</td>
                          <td>{new Date(email.createdAt).toLocaleDateString()}</td>
                          <td>
                            {!email.isUsed && (
                              <button 
                                onClick={() => handleRemoveEmail(email._id)}
                                className="action-btn remove"
                              >
                                Remove
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="settings-tab">
              <h3>Chat Settings</h3>
              
              {loading ? (
                <div className="loading">Loading settings...</div>
              ) : chatSettings ? (
                <div className="settings-section">
                  <div className="setting-item">
                    <div className="setting-info">
                      <h4>Announcement Mode</h4>
                      <p>
                        When enabled, only admins and moderators can send messages. 
                        Members can only read messages.
                      </p>
                      {chatSettings.announcementMode && chatSettings.enabledBy && (
                        <small>
                          Enabled by {chatSettings.enabledBy.displayName} on{' '}
                          {new Date(chatSettings.enabledAt).toLocaleString()}
                        </small>
                      )}
                    </div>
                    <div className="setting-control">
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={chatSettings.announcementMode}
                          onChange={toggleAnnouncementMode}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                      <span className={`status-text ${chatSettings.announcementMode ? 'enabled' : 'disabled'}`}>
                        {chatSettings.announcementMode ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={cancelAction}
        onConfirm={confirmAction}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Remove"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default Admin;